import { createContext, useContext } from 'react';
import type Keycloak from 'keycloak-js';

/**
 * Context value for Keycloak authentication state
 */
export interface AuthContextValue {
  /** Keycloak instance (null if not initialized) */
  keycloak: Keycloak | null;
  /** Whether Keycloak has been initialized */
  initialized: boolean;
  /** Whether user is authenticated */
  authenticated: boolean;
  /** Token version - incremented when token is refreshed to trigger re-renders */
  tokenVersion: number;
  /** Force token refresh and trigger re-render */
  forceTokenRefresh: () => Promise<boolean>;
}

/**
 * Default context value
 */
const defaultValue: AuthContextValue = {
  keycloak: null,
  initialized: false,
  authenticated: false,
  tokenVersion: 0,
  forceTokenRefresh: async () => false,
};

/**
 * React context for Keycloak authentication state
 */
export const KeycloakContext = createContext<AuthContextValue>(defaultValue);

/**
 * Hook to access Keycloak context.
 * Throws an error if used outside of AuthProvider.
 * 
 * @returns Keycloak context value
 * @throws Error if used outside AuthProvider
 */
export function useKeycloakContext(): AuthContextValue {
  const context = useContext(KeycloakContext);
  
  if (!context) {
    throw new Error('useKeycloakContext must be used within a AuthProvider');
  }
  
  return context;
}
